import type { ChatAttachmentUpload } from '@newfront-insurance/ai-api';
import type { ToolUIPart } from 'ai';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export { getBrowserTimezone } from './browser-timezone';

/**
 * Temporary local mirror of the stable @newfront-insurance/ai-api schedule proposal contract.
 * Replace these declarations with the published package exports when the backend stack merges.
 */
export const NOVA_SCHEDULE_RECURRENCE_PRESETS = ['ONCE', 'HOURLY', 'DAILY', 'WEEKDAYS', 'WEEKLY', 'MONTHLY'] as const;

export type NovaScheduleRecurrencePreset = (typeof NOVA_SCHEDULE_RECURRENCE_PRESETS)[number];

export const NOVA_SCHEDULE_WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export type NovaScheduleWeekday = (typeof NOVA_SCHEDULE_WEEKDAYS)[number];
export type NovaScheduleOutputPolicy = 'APPEND_TO_CHAT' | 'NEW_CHAT_PER_RUN';
export type NovaScheduleStatus = 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'COMPLETED';
export const NOVA_SCHEDULES_FEATURE_FLAG = 'nova-tools-schedules';
export const NO_ACCOUNT_SELECTED_SCHEDULE_LABEL = 'No account';

export interface NovaScheduleRecurrence {
  preset: NovaScheduleRecurrencePreset;
  minuteOfHour?: number;
  timeOfDay?: string;
  weekdays?: NovaScheduleWeekday[];
  weekInterval?: number;
  dayOfMonth?: number;
}

export interface StoredNovaScheduleProposal {
  title: string;
  prompt: string;
  /** Workspace files every run attaches (the chat's inputs the proposal names). */
  attachmentPaths?: string[];
  timezone: string;
  recurrence: NovaScheduleRecurrence;
  startAt?: string;
  outputPolicy?: NovaScheduleOutputPolicy;
  preview?: {
    firstRunAt: string | null;
  };
}

export type NovaScheduleProposalDraft = Omit<StoredNovaScheduleProposal, 'preview'> & {
  /** Files uploaded while reviewing the proposal; committed when it's confirmed. */
  attachmentUploads?: ChatAttachmentUpload[];
};

export interface NovaScheduleProposalScheduleSummary {
  uuid: string;
  title: string;
  prompt: string;
  timezone: string;
  recurrence: NovaScheduleRecurrence;
  status: NovaScheduleStatus;
  statusReason: string | null;
  outputPolicy: NovaScheduleOutputPolicy;
  startAt: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  accountUuid: string | null;
}

export type ResolvedNovaScheduleProposalOutput =
  | {
      action: 'confirm';
      scheduleUuid: string;
      message?: string;
      schedule: NovaScheduleProposalScheduleSummary;
    }
  | { action: 'cancel' };

export type NovaScheduleProposalResolution =
  | {
      action: 'confirm';
      scheduleUuid: string;
      message?: string;
    }
  | { action: 'cancel' };

type NovaScheduleProposalUITools = {
  proposeNovaSchedule: {
    input: StoredNovaScheduleProposal;
    output: NovaScheduleProposalResolution | ResolvedNovaScheduleProposalOutput;
  };
};

export const NOVA_SCHEDULE_PROPOSAL_TOOL_NAME = 'proposeNovaSchedule';

export type NovaScheduleProposalToolPart = ToolUIPart<NovaScheduleProposalUITools>;
export type PendingNovaScheduleProposalToolPart = Extract<NovaScheduleProposalToolPart, { state: 'input-available' }>;
export type ResolvedNovaScheduleProposalToolPart = Extract<NovaScheduleProposalToolPart, { state: 'output-available' }>;

export function isNovaScheduleProposalToolPart(part: { type: string }): part is NovaScheduleProposalToolPart {
  return part.type === `tool-${NOVA_SCHEDULE_PROPOSAL_TOOL_NAME}`;
}

export function isResolvedNovaScheduleProposalOutput(
  output: NovaScheduleProposalResolution | ResolvedNovaScheduleProposalOutput,
): output is ResolvedNovaScheduleProposalOutput {
  return output.action === 'cancel' || 'schedule' in output;
}

const PRESET_DEFAULTS: Record<NovaScheduleRecurrencePreset, NovaScheduleRecurrence> = {
  ONCE: { preset: 'ONCE' },
  HOURLY: { preset: 'HOURLY', minuteOfHour: 0 },
  DAILY: { preset: 'DAILY', timeOfDay: '09:00' },
  WEEKDAYS: { preset: 'WEEKDAYS', timeOfDay: '09:00' },
  WEEKLY: { preset: 'WEEKLY', timeOfDay: '09:00', weekdays: ['MO'] },
  MONTHLY: { preset: 'MONTHLY', timeOfDay: '09:00', dayOfMonth: 1 },
};

const WEEKDAY_LABELS: Record<NovaScheduleWeekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

export function buildNovaScheduleProposalDraft(proposal: StoredNovaScheduleProposal): NovaScheduleProposalDraft {
  const isOneTime = proposal.recurrence.preset === 'ONCE';

  return {
    title: proposal.title,
    prompt: proposal.prompt,
    attachmentPaths: proposal.attachmentPaths?.slice(),
    timezone: proposal.timezone,
    recurrence: { ...proposal.recurrence, weekdays: proposal.recurrence.weekdays?.slice() },
    startAt: proposal.startAt,
    outputPolicy: isOneTime ? undefined : (proposal.outputPolicy ?? 'APPEND_TO_CHAT'),
  };
}

export function hasNovaScheduleTimingChanged(
  proposal: StoredNovaScheduleProposal,
  draft: NovaScheduleProposalDraft,
): boolean {
  const proposalWeekdays = proposal.recurrence.weekdays ?? [];
  const draftWeekdays = draft.recurrence.weekdays ?? [];

  return (
    proposal.timezone !== draft.timezone ||
    (proposal.recurrence.preset === 'ONCE' && proposal.startAt !== draft.startAt) ||
    proposal.recurrence.preset !== draft.recurrence.preset ||
    proposal.recurrence.minuteOfHour !== draft.recurrence.minuteOfHour ||
    proposal.recurrence.timeOfDay !== draft.recurrence.timeOfDay ||
    (proposal.recurrence.weekInterval ?? 1) !== (draft.recurrence.weekInterval ?? 1) ||
    proposal.recurrence.dayOfMonth !== draft.recurrence.dayOfMonth ||
    proposalWeekdays.length !== draftWeekdays.length ||
    proposalWeekdays.some((weekday, index) => weekday !== draftWeekdays[index])
  );
}

export function changeNovaScheduleRecurrencePreset(
  recurrence: NovaScheduleRecurrence,
  preset: NovaScheduleRecurrencePreset,
): NovaScheduleRecurrence {
  const defaults = PRESET_DEFAULTS[preset];

  return {
    ...defaults,
    ...(defaults.timeOfDay && recurrence.timeOfDay ? { timeOfDay: recurrence.timeOfDay } : {}),
    ...(defaults.minuteOfHour !== undefined && recurrence.minuteOfHour !== undefined
      ? { minuteOfHour: recurrence.minuteOfHour }
      : {}),
    ...(defaults.weekdays && recurrence.weekdays?.length ? { weekdays: recurrence.weekdays.slice() } : {}),
    ...(defaults.dayOfMonth !== undefined && recurrence.dayOfMonth !== undefined
      ? { dayOfMonth: recurrence.dayOfMonth }
      : {}),
  };
}

export function validateNovaScheduleProposalDraft(
  draft: NovaScheduleProposalDraft,
  now = new Date(),
): string | undefined {
  if (!draft.title.trim()) return 'Add a title before creating the schedule.';
  if (draft.title.trim().length > 200) return 'Keep the title to 200 characters or fewer.';
  if (!draft.prompt.trim()) return 'Add instructions for Nova before creating the schedule.';
  if (!draft.timezone.trim()) return 'Add an IANA time zone before creating the schedule.';

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: draft.timezone }).format();
  } catch {
    return 'Use a valid IANA time zone, such as America/Los_Angeles.';
  }

  const { recurrence } = draft;

  if (recurrence.preset === 'ONCE') {
    const startAtTimestamp = Date.parse(draft.startAt ?? '');
    if (Number.isNaN(startAtTimestamp)) {
      return 'Choose when the one-time reminder should run.';
    }
    const startAtMinute = Math.floor(startAtTimestamp / 60_000) * 60_000;
    if (startAtMinute <= now.getTime()) {
      return 'Choose a future date and time.';
    }
    return undefined;
  }

  if (recurrence.preset === 'HOURLY') {
    if (
      recurrence.minuteOfHour === undefined ||
      !Number.isInteger(recurrence.minuteOfHour) ||
      recurrence.minuteOfHour < 0 ||
      recurrence.minuteOfHour > 59
    ) {
      return 'Choose a minute from 0 through 59.';
    }
  } else if (!recurrence.timeOfDay || !/^([01]\d|2[0-3]):[0-5]\d$/.test(recurrence.timeOfDay)) {
    return 'Choose a valid time of day.';
  }

  if (recurrence.preset === 'WEEKLY' && !recurrence.weekdays?.length) {
    return 'Choose at least one weekday.';
  }

  if (
    recurrence.preset === 'WEEKLY' &&
    recurrence.weekInterval !== undefined &&
    (!Number.isInteger(recurrence.weekInterval) || recurrence.weekInterval < 1 || recurrence.weekInterval > 4)
  ) {
    return 'Choose a repeat interval from 1 through 4 weeks.';
  }

  if (
    recurrence.preset === 'MONTHLY' &&
    (recurrence.dayOfMonth === undefined ||
      !Number.isInteger(recurrence.dayOfMonth) ||
      recurrence.dayOfMonth < 1 ||
      recurrence.dayOfMonth > 31)
  ) {
    return 'Choose a day from 1 through 31.';
  }

  return undefined;
}

export function formatNovaScheduleRecurrence(recurrence: NovaScheduleRecurrence): string {
  switch (recurrence.preset) {
    case 'ONCE':
      return 'One time';
    case 'HOURLY':
      return `Every hour at ${recurrence.minuteOfHour ?? 0} minutes past`;
    case 'DAILY':
      return `Every day at ${formatTimeOfDay(recurrence.timeOfDay)}`;
    case 'WEEKDAYS':
      return `Every weekday at ${formatTimeOfDay(recurrence.timeOfDay)}`;
    case 'WEEKLY': {
      const weekdays = recurrence.weekdays?.map((weekday) => WEEKDAY_LABELS[weekday]).join(', ') || 'No days selected';
      const interval = recurrence.weekInterval ?? 1;
      const cadence =
        interval === 1
          ? `Every ${weekdays}`
          : interval === 2 && recurrence.weekdays?.length === 1
            ? `Every other ${weekdays}`
            : `Every ${interval} weeks on ${weekdays}`;
      return `${cadence} at ${formatTimeOfDay(recurrence.timeOfDay)}`;
    }
    case 'MONTHLY':
      return `Every month on day ${recurrence.dayOfMonth ?? 1} at ${formatTimeOfDay(recurrence.timeOfDay)}`;
  }
}

export function formatNovaScheduleDateTime(value: string | null | undefined, timezone: string): string {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return value;
  }
}

export function toNovaScheduleDateTimeInput(value: string | undefined, timezone: string): string {
  if (!value) return '';

  try {
    return formatInTimeZone(new Date(value), timezone, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

export function getNovaScheduleMinimumDateTimeInput(timezone: string, now = new Date()): string {
  const nextMinute = new Date(Math.floor(now.getTime() / 60_000) * 60_000 + 60_000);
  return toNovaScheduleDateTimeInput(nextMinute.toISOString(), timezone);
}

export function fromNovaScheduleDateTimeInput(value: string, timezone: string): string | undefined {
  if (!value) return undefined;

  try {
    const utcDate = zonedTimeToUtc(value, timezone);
    return Number.isNaN(utcDate.getTime()) ? undefined : utcDate.toISOString();
  } catch {
    return undefined;
  }
}

function formatTimeOfDay(value: string | undefined): string {
  const match = /^(\d{2}):(\d{2})$/.exec(value ?? '');
  if (!match) return 'time not set';

  const hour = Number(match[1]);
  const minute = match[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${period}`;
}
